"use client";

import { useEffect, useRef, useState } from "react";

const steps = [
  {
    number: "01",
    title: "Initial Consultation",
    description:
      "We begin by understanding your specific needs—whether it's for tax purposes, estate settlement, divorce proceedings, pre-listing, or purchase decisions. We'll clarify the intended use, identify the property type, discuss any unique circumstances, confirm the effective date of value, and provide a transparent fee quote with no hidden costs.",
    duration: "Same day",
  },
  {
    number: "02",
    title: "Schedule Appraisal Appointment",
    description:
      "We coordinate a convenient appointment time that works with your schedule. Prior to the visit, we gather preliminary information including legal descriptions, tax records, and any relevant documents you can provide such as surveys, renovation receipts, or previous appraisals.",
    duration: "1-3 days",
  },
  {
    number: "03",
    title: "On-Site Property Visit",
    description:
      "A comprehensive inspection covering 20+ factors: room dimensions, layout, construction quality, foundation, roof condition, electrical, plumbing, windows, flooring, kitchen and bath finishes, basement development, landscaping, curb appeal, site improvements, workmanship quality, functional utility, and overall property condition.",
    duration: "1 hour+",
  },
  {
    number: "04",
    title: "Market Research & Analysis",
    description:
      "We identify and analyze comparable sales in your area, researching each property's features, improvements, sale conditions, and market exposure. Detailed adjustments are made for differences in size, age, quality, location, upgrades, and market trends.",
    duration: "1-2 days",
  },
  {
    number: "05",
    title: "Report Preparation",
    description:
      "Your appraisal report is carefully prepared following CUSPAP standards. This includes detailed property descriptions, neighbourhood analysis, comparable sales data with adjustment grids, supporting photographs, site sketches, and a thoroughly reasoned opinion of market value as of the effective date.",
    duration: "1-2 days",
  },
  {
    number: "06",
    title: "Report Delivery",
    description:
      "Your completed appraisal report is delivered electronically in PDF format. We review the key findings with you, explain the methodology and comparable sales used, and remain available to answer any follow-up questions from you, your lawyer, accountant, or other authorized parties.",
    duration: "Same day",
  },
];

export default function ProcessSteps() {
  const [activeStep, setActiveStep] = useState(0);
  const sectionRef = useRef(null);
  const stepRefs = useRef([]);

  useEffect(() => {
    const observers = stepRefs.current.map((ref, index) => {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              setActiveStep(index);
            }
          });
        },
        {
          threshold: 0.6,
          rootMargin: "-20% 0px -20% 0px",
        }
      );

      if (ref) {
        observer.observe(ref);
      }

      return observer;
    });

    return () => {
      observers.forEach((observer) => observer.disconnect());
    };
  }, []);

  return (
    <section
      ref={sectionRef}
      className="relative bg-gradient-to-b from-gray-50 to-white dark:from-gray-800/50 dark:to-gray-900"
    >
      {/* Header */}
      <div className="sticky top-0 z-10 bg-gradient-to-b from-gray-50 via-gray-50 to-transparent dark:from-gray-800/50 dark:via-gray-800/50 pt-16 pb-8">
        <div className="max-w-6xl mx-auto px-6 text-center">
          <span className="text-blue-600 dark:text-blue-400 text-sm font-semibold uppercase tracking-wider">
            From Start to Finish
          </span>
          <h2 className="text-3xl sm:text-4xl font-bold mt-2 text-gray-900 dark:text-white">
            The Appraisal Process
          </h2>
        </div>

        {/* Progress Indicator */}
        <div className="max-w-4xl mx-auto px-6 mt-8">
          <div className="flex items-center justify-between">
            {steps.map((step, index) => (
              <div key={index} className="flex items-center">
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm transition-all duration-500 ${
                    index <= activeStep
                      ? "bg-blue-600 text-white scale-110"
                      : "bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400"
                  }`}
                >
                  {index + 1}
                </div>
                {index < steps.length - 1 && (
                  <div className="hidden sm:block w-12 md:w-20 lg:w-28 h-1 mx-1">
                    <div
                      className={`h-full rounded-full transition-all duration-500 ${
                        index < activeStep
                          ? "bg-blue-600"
                          : "bg-gray-200 dark:bg-gray-700"
                      }`}
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Steps Content */}
      <div className="max-w-4xl mx-auto px-6 pb-20">
        {steps.map((step, index) => (
          <div
            key={index}
            ref={(el) => (stepRefs.current[index] = el)}
            className="py-8"
          >
            {/* Step Card with border on active */}
            <div
              className={`relative p-6 sm:p-8 rounded-2xl transition-all duration-500 ${
                index === activeStep
                  ? "bg-white dark:bg-gray-800/50 border-2 border-blue-500 shadow-lg shadow-blue-500/10 scale-[1.02]"
                  : "bg-transparent border-2 border-transparent opacity-50 scale-100"
              }`}
            >
              {/* Active indicator dot */}
              {index === activeStep && (
                <div className="absolute -left-1 top-1/2 -translate-y-1/2 w-2 h-12 bg-blue-500 rounded-full" />
              )}

              <div className="flex items-center gap-4 sm:gap-6">
                {/* Number */}
                <div className={`flex-shrink-0 ${index % 2 === 1 ? "md:order-2" : ""}`}>
                  <div className="relative">
                    <span className={`text-[120px] sm:text-[160px] md:text-[180px] font-bold leading-none transition-colors duration-500 ${
                      index === activeStep
                        ? "text-blue-300 dark:text-blue-700"
                        : "text-gray-200 dark:text-gray-800"
                    }`}>
                      {step.number}
                    </span>
                    <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2">
                      <span
                        className={`inline-block px-3 py-1.5 rounded-full text-xs sm:text-sm font-semibold whitespace-nowrap transition-all duration-500 ${
                          index === activeStep
                            ? "bg-blue-600 text-white shadow-md"
                            : "bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400"
                        }`}
                      >
                        {step.duration}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Content */}
                <div className={`flex-1 ${index % 2 === 1 ? "md:order-1 md:text-right" : ""}`}>
                  <h3 className={`text-2xl sm:text-3xl font-bold mb-3 transition-colors duration-500 ${
                    index === activeStep
                      ? "text-gray-900 dark:text-white"
                      : "text-gray-600 dark:text-gray-400"
                  }`}>
                    {step.title}
                  </h3>
                  <p className={`text-base sm:text-lg leading-relaxed transition-colors duration-500 ${
                    index === activeStep
                      ? "text-gray-600 dark:text-gray-300"
                      : "text-gray-500 dark:text-gray-500"
                  }`}>
                    {step.description}
                  </p>
                </div>
              </div>
            </div>

            {/* Divider */}
            {index < steps.length - 1 && (
              <div className="flex justify-center mt-8">
                <div className={`w-px h-12 transition-all duration-500 ${
                  index < activeStep
                    ? "bg-blue-400"
                    : "bg-gray-200 dark:bg-gray-700"
                }`}></div>
              </div>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}
